import { useCallback, useState } from 'react'
import { GetPresignedUrlCrmo } from 'domain/usecases/surgical-order/get-presigned-url-crmo'
import { UploadFileToS3 } from 'domain/usecases/surgical-order/upload-file-to-s3'
import { ProcessDocumentCrmo } from 'domain/usecases/surgical-order/process-document-crmo'

export type CrmoFileToUpload = {
  file: File
  documentName: string
  documentType: string
}

export type UseCrmoDocumentUploadParams = {
  groupId: string
  createdBy: number
  createdByName: string
  getPresignedUrl: (
    params: GetPresignedUrlCrmo.Params
  ) => Promise<GetPresignedUrlCrmo.Model>
  uploadFileToS3: (params: UploadFileToS3.Params) => Promise<void>
  processDocument: (
    params: ProcessDocumentCrmo.Params
  ) => Promise<ProcessDocumentCrmo.Model>
}

export type UseCrmoDocumentUploadReturn = {
  isUploading: boolean
  uploadFiles: (files: CrmoFileToUpload[]) => Promise<{ hasError: boolean }>
}

/**
 * Upload de documentos do pedido cirúrgico (CRMO) executado por arquivo,
 * no momento em que o usuário submete o arquivo — antes de o pedido existir.
 * Os arquivos são agrupados por `groupId` e mesclados ao pedido no final,
 * via mergeDocumentsCrmo(group_id, surgical_order_id).
 *
 * Fluxo por arquivo: getPresignedUrlCrmo -> uploadFileToS3 -> processDocumentCrmo
 */
export function useCrmoDocumentUpload(
  params: UseCrmoDocumentUploadParams
): UseCrmoDocumentUploadReturn {
  const {
    groupId,
    createdBy,
    createdByName,
    getPresignedUrl,
    uploadFileToS3,
    processDocument
  } = params

  const [isUploading, setIsUploading] = useState(false)

  const uploadSingleFile = useCallback(
    async (fileToUpload: CrmoFileToUpload) => {
      const presigned = await getPresignedUrl({
        document_name: fileToUpload.documentName,
        document_type: fileToUpload.file.type,
        group_id: groupId
      })

      await uploadFileToS3({
        url: presigned.url,
        file: fileToUpload.file,
        contentType: fileToUpload.file.type
      })

      await processDocument({
        document_name: fileToUpload.documentName,
        document_type: fileToUpload.documentType,
        document_url: presigned.key,
        created_by: createdBy,
        created_by_name: createdByName,
        group_id: groupId
      })
    },
    [
      groupId,
      createdBy,
      createdByName,
      getPresignedUrl,
      uploadFileToS3,
      processDocument
    ]
  )

  const uploadFiles = useCallback(
    async (files: CrmoFileToUpload[]) => {
      if (files.length === 0) {
        return { hasError: false }
      }

      setIsUploading(true)
      try {
        const results = await Promise.allSettled(files.map(uploadSingleFile))
        return {
          hasError: results.some((result) => result.status === 'rejected')
        }
      } finally {
        setIsUploading(false)
      }
    },
    [uploadSingleFile]
  )

  return { isUploading, uploadFiles }
}
